"use client";

import { useLocale } from "next-intl";
import LegalLayout, { type LegalSection } from "./LegalLayout";

const SECTIONS = [
  {
    titleAr: "المعلومات التي نجمعها",
    titleEn: "Information we collect",
    htmlAr: "<p>نجمع المعلومات التي تقدّمها مباشرةً عند إنشاء حساب أو إتمام حجز، مثل الاسم والبريد الإلكتروني ورقم الجوال وتفاصيل الدفع. كما نجمع بيانات الاستخدام تلقائيًا لتحسين تجربتك.</p>",
    htmlEn: "<p>We collect information you provide directly when creating an account or completing a booking, such as your name, email, phone number and payment details. We also automatically collect usage data to improve your experience.</p>",
  },
  {
    titleAr: "كيف نستخدم معلوماتك",
    titleEn: "How we use your information",
    htmlAr: "<ul><li>معالجة الحجوزات والمدفوعات.</li><li>التواصل معك بخصوص طلباتك وحسابك.</li><li>تحسين خدماتنا وتخصيص تجربتك.</li><li>ضمان أمان المنصّة ومنع الاحتيال.</li></ul>",
    htmlEn: "<ul><li>Process bookings and payments.</li><li>Communicate with you about your requests and account.</li><li>Improve our services and personalize your experience.</li><li>Keep the platform secure and prevent fraud.</li></ul>",
  },
  {
    titleAr: "مشاركة المعلومات",
    titleEn: "Sharing your information",
    htmlAr: "<p>لا نبيع بياناتك الشخصية. قد نشارك بعض المعلومات مع الملّاك ومزوّدي الخدمات لإتمام حجزك، ومع مزوّدي خدمات الدفع الموثوقين، وذلك بالحد الأدنى اللازم فقط.</p>",
    htmlEn: "<p>We do not sell your personal data. We may share limited information with owners and providers to complete your booking, and with trusted payment processors — only to the extent necessary.</p>",
  },
  {
    titleAr: "بيانات الهوية والتسجيل في منصة شموس",
    titleEn: "Identity data & the Shomoos platform",
    htmlAr:
      "<p>وفقًا للأنظمة المعمول بها في المملكة العربية السعودية، تلتزم منشآت الإيواء السياحي بتسجيل بيانات النزلاء في <strong>منصة شموس</strong> التابعة للجهات الرسمية. لذلك نطلب منك عند الحجز تقديم بيانات الهوية التالية: نوع الهوية ورقمها (هوية وطنية / إقامة / جواز سفر / هوية دول الخليج)، الجنسية، تاريخ الميلاد، والجنس، وفي حال جواز السفر تاريخ الانتهاء والدولة المُصدِرة.</p><ul><li>تُستخدم هذه البيانات <strong>حصريًا</strong> لإصدار عقد التسكين وتسجيل إقامتك لدى الجهات الرسمية المختصة ونظام إدارة المنشأة.</li><li>لا تُستخدم لأغراض تسويقية ولا تُشارك مع أي طرف تجاري ثالث.</li><li>تُخزَّن بشكل مشفَّر وتُعرض في حسابك وفي لوحة إدارة المنشأة بالحد الأدنى اللازم.</li><li>يحقّ لك طلب الاطلاع على هذه البيانات أو تصحيحها، مع مراعاة أن الاحتفاظ بها لفترة محددة قد يكون مطلوبًا نظاميًا.</li></ul>",
    htmlEn:
      "<p>Under applicable regulations in the Kingdom of Saudi Arabia, tourist accommodation facilities must register guest details with the official <strong>Shomoos</strong> platform. When booking, we therefore ask for the following identity details: ID type and number (National ID / Iqama / Passport / GCC ID), nationality, date of birth and gender — and for passports, the expiry date and issuing country.</p><ul><li>These details are used <strong>exclusively</strong> to issue your accommodation contract and register your stay with the competent authorities and the property-management system.</li><li>They are never used for marketing and are never shared with any commercial third party.</li><li>They are stored encrypted and shown in your account and the facility dashboard only to the minimum extent necessary.</li><li>You may request access to or correction of these details; note that retaining them for a defined period may be legally required.</li></ul>",
  },
  {
    titleAr: "أمان البيانات",
    titleEn: "Data security",
    htmlAr: "<p>نستخدم إجراءات حماية تقنية وتنظيمية، بما في ذلك التشفير، للحفاظ على أمان بياناتك. ومع ذلك، لا يمكن ضمان أمان أي وسيلة نقل عبر الإنترنت بنسبة 100%.</p>",
    htmlEn: "<p>We use technical and organizational safeguards, including encryption, to keep your data secure. However, no method of transmission over the internet can be guaranteed 100% secure.</p>",
  },
  {
    titleAr: "حقوقك",
    titleEn: "Your rights",
    htmlAr: "<p>يحقّ لك الوصول إلى بياناتك الشخصية وتصحيحها أو حذفها في أي وقت من خلال إعدادات حسابك أو بالتواصل مع فريق الدعم.</p>",
    htmlEn: "<p>You have the right to access, correct or delete your personal data at any time through your account settings or by contacting our support team.</p>",
  },
  {
    titleAr: "ملفات تعريف الارتباط (الكوكيز)",
    titleEn: "Cookies",
    htmlAr: "<p>نستخدم ملفات تعريف الارتباط الضرورية لتشغيل المنصّة وتذكّر تفضيلاتك. يمكنك التحكّم في الكوكيز من إعدادات متصفّحك.</p>",
    htmlEn: "<p>We use essential cookies to run the platform and remember your preferences. You can control cookies from your browser settings.</p>",
  },
  {
    titleAr: "التواصل معنا",
    titleEn: "Contact us",
    htmlAr: "<p>لأي استفسار يخصّ الخصوصية، يمكنك التواصل مع فريق الدعم عبر صفحة المساعدة.</p>",
    htmlEn: "<p>For any privacy-related questions, you can reach our support team via the Help page.</p>",
  },
];

export default function PrivacyClient() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const sections: LegalSection[] = SECTIONS.map((s) => ({
    title: isAr ? s.titleAr : s.titleEn,
    html: isAr ? s.htmlAr : s.htmlEn,
  }));

  return (
    <LegalLayout
      title={isAr ? "سياسة الخصوصية" : "Privacy Policy"}
      subtitle={isAr ? "نحن نحترم خصوصيتك ونلتزم بحماية بياناتك الشخصية." : "We respect your privacy and are committed to protecting your personal data."}
      note={isAr ? "آخر تحديث: أغسطس 2026" : "Last updated: August 2026"}
      sections={sections}
    />
  );
}
